import { useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useAuthStore } from './store/authStore'

// Pages
import { LandingPage } from './pages/Landing'
import { LoginPage } from './pages/Login'
import { AuthCallbackPage } from './pages/AuthCallback'
import { DashboardLayout } from './components/DashboardLayout'
import { HomePage } from './pages/Home'
import { NudgeLibraryPage } from './pages/NudgeLibrary'
import { AnalyticsPage } from './pages/Analytics'
import { TeamPage } from './pages/Team'
import { SettingsPage } from './pages/Settings'
import { AccountabilityPage } from './pages/Accountability'
import { NudgeHistoryPage } from './pages/NudgeHistory'
import { SecurityPage } from './pages/Security'
import { PrivacyPage } from './pages/Privacy'
import { TermsPage } from './pages/Terms'
import { PricingScreen } from './pages/Pricing'
import { PlatformAdminPage } from './pages/PlatformAdmin'
import { NudgeStudioPage } from './pages/NudgeStudio'
import { FeedbackThemesPage } from './pages/FeedbackThemes'
import { BehavioralRoiMethodologyPage } from './pages/BehavioralRoiMethodology'
import { InviteAcceptRedirectPage } from './pages/InviteAcceptRedirect'
import { InviteExpiredPage } from './pages/InviteExpiredPage'
import { ToastContainer } from './components/ToastContainer'

function AuthLoadingScreen() {
  return (
    <div
      className="flex flex-col items-center justify-center gap-4 h-screen"
      style={{ background: '#F5F2EA', color: '#2A7A8A' }}
    >
      <div className="text-4xl animate-pulse" aria-hidden>
        🕊
      </div>
      <div className="flex flex-col items-center gap-1">
        <p className="font-semibold text-base" style={{ fontFamily: 'Georgia, serif', color: '#1a3a40' }}>
          Duva
        </p>
        <p className="text-sm" style={{ color: '#6b8a8f' }}>
          Loading your workspace…
        </p>
      </div>
      <div
        className="h-1 w-32 rounded-full overflow-hidden"
        style={{ background: '#e0d9cc' }}
        aria-hidden
      >
        <div
          className="h-full w-1/3 rounded-full animate-pulse"
          style={{ background: '#2A7A8A', animationDuration: '1.2s' }}
        />
      </div>
    </div>
  )
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuthStore()

  if (loading) return <AuthLoadingScreen />
  if (!user) return <Navigate to="/login" replace />
  return <>{children}</>
}

function RequireAdmin({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore()
  if (user?.role !== 'admin') return <Navigate to="/dashboard" replace />
  return <>{children}</>
}

/** Org-wide metrics (not personal). Employees use Home + History for their own activity. */
function RequireManagerOrAdmin({ children }: { children: React.ReactNode }) {
  const { user } = useAuthStore()
  if (user?.role !== 'admin' && user?.role !== 'manager') {
    return <Navigate to="/dashboard" replace />
  }
  return <>{children}</>
}

export default function App() {
  const fetchMe = useAuthStore(s => s.fetchMe)

  useEffect(() => {
    // Restore session from localStorage token on mount
    const token = localStorage.getItem('duva_token')
    if (token) fetchMe()
  }, [fetchMe])

  return (
    <>
    <Routes>
      {/* Public */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallbackPage />} />
      <Route path="/invite/accept" element={<InviteAcceptRedirectPage />} />
      <Route path="/invite/expired" element={<InviteExpiredPage />} />
      <Route path="/security" element={<SecurityPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/admin" element={<PlatformAdminPage />} />
      <Route path="/admin/studio" element={<NudgeStudioPage />} />

      {/* Public doc (shared reports link here; must not require login) */}
      <Route path="/dashboard/behavioral-roi-methodology" element={<BehavioralRoiMethodologyPage />} />

      {/* Protected — wrapped in dashboard layout */}
      <Route path="/dashboard" element={
        <RequireAuth>
          <DashboardLayout />
        </RequireAuth>
      }>
        <Route index element={<HomePage />} />
        <Route
          path="library"
          element={
            <RequireAdmin>
              <NudgeLibraryPage />
            </RequireAdmin>
          }
        />
        <Route
          path="analytics"
          element={
            <RequireManagerOrAdmin>
              <AnalyticsPage />
            </RequireManagerOrAdmin>
          }
        />
        <Route
          path="feedback-themes"
          element={
            <RequireAdmin>
              <FeedbackThemesPage />
            </RequireAdmin>
          }
        />
        <Route path="team" element={<TeamPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="accountability" element={<AccountabilityPage />} />
        <Route path="history" element={<NudgeHistoryPage />} />
        <Route
          path="pricing"
          element={
            <RequireAdmin>
              <PricingScreen />
            </RequireAdmin>
          }
        />
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    <ToastContainer />
    </>
  )
}
